import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { ExitTicketCardProps } from "./ExitTicketCard";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  ticketContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#fdf2f8",
    borderRadius: 8,
  },
  ticketTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#db2777",
  },
  prompt: {
    fontSize: 10,
    color: "#374151",
    lineHeight: 1.4,
  },
  responseItem: {
    flexDirection: "row",
    marginBottom: 4,
    paddingLeft: 8,
  },
  bullet: {
    width: 10,
    fontSize: 9,
    color: "#db2777",
  },
});

const ExitTicketDocument = ({
  exitTickets,
  imageUrl,
  courseName,
  gradeLevel,
  topic,
  authorName,
  createdAt,
  customizationSettings,
}: ExitTicketCardProps) => {
  const { t } = useTranslation();

  return (
    <PdfLayout
      courseName={courseName}
      gradeLevel={gradeLevel}
      topic={topic}
      imageUrl={imageUrl}
      headerTextColor="#db2777"
      author={authorName}
      createdDate={createdAt}
      customizationSettings={customizationSettings}
    >
      {exitTickets?.map((ticket, index) => (
        <View key={index} style={styles.ticketContainer} wrap={false}>
          <MarkdownRenderer
            content={ticket.ticket_title}
            customStyle={{
              text: {
                text: {
                  ...styles.ticketTitle,
                  fontSize: scaleFontSize(
                    styles.ticketTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                  fontWeight: "bold",
                },
                container: {
                  marginBottom: 8,
                },
              },
            }}
          />
          <MarkdownRenderer
            content={ticket.ticket_prompt}
            customStyle={{
              text: {
                text: {
                  ...styles.prompt,
                  fontSize: scaleFontSize(
                    styles.prompt.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
                container: {
                  marginBottom: 8,
                },
              },
            }}
          />

          {ticket.recommended_format && (
            <>
              <Text
                style={[
                  commonStyles.sectionTitle,
                  {
                    fontSize: scaleFontSize(
                      commonStyles.sectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("exitTickets.recommendedFormat")}:
              </Text>
              <MarkdownRenderer
                content={ticket.recommended_format}
                customStyle={{
                  text: {
                    text: {
                      ...commonStyles.sectionContent,
                      fontSize: scaleFontSize(
                        commonStyles.sectionContent.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  },
                }}
              />
            </>
          )}

          {ticket.possible_responses && (
            <>
              <Text
                style={[
                  commonStyles.sectionTitle,
                  {
                    fontSize: scaleFontSize(
                      commonStyles.sectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("exitTickets.possibleResponses")}:
              </Text>
              {ticket.possible_responses.map((response, responseIndex) => (
                <View key={responseIndex} style={styles.responseItem}>
                  <Text style={styles.bullet}>•</Text>
                  <MarkdownRenderer
                    content={
                      response.charAt(0).toUpperCase() + response.slice(1)
                    }
                    customStyle={{
                      text: {
                        text: {
                          ...commonStyles.sectionContent,
                          fontSize: scaleFontSize(
                            commonStyles.sectionContent.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                      },
                    }}
                  />
                </View>
              ))}
            </>
          )}
        </View>
      ))}
    </PdfLayout>
  );
};

function ExitTicketCardPDF(props: ExitTicketCardProps) {
  return <ExitTicketDocument {...props} />;
}

export default ExitTicketCardPDF;
